import type { StayFull, StayStage, StayStageKey } from '../interfaces/stays';
import { formatDayKey } from '../function-utils/building-time';

/**
 * LOBBERO STAYS — a stay's journey bar and timing chip (ai-plans/stays plan S4,
 * R-S4.25). The SERVER decides the stage (lobbero_be/src/stays/stage.js, on the
 * building's clock); this file only turns it into a percentage and words.
 *
 * The hour fed in is the BUILDING's wall-clock hour (the envelope's
 * `building_now`), never the device's — Konios used the browser's hour, the
 * bug this replaces. The cases are a COPY of lobbero_be/scripts/stays/
 * stage-cases.json (`bar`), kept by check-fe-copy.js.
 */

export interface JourneyInput {
  key: StayStageKey;
  days_until?: number;
  night_index?: number | null;
  nights?: number;
  /** Building wall clock, fractional hours (14.5 = 14:30). */
  hour?: number;
  /** The departure wall time as fractional hours (11 = 11:00). */
  departure_hour?: number;
}

export const journeyBar = (s: JourneyInput): { pct: number; text: string } => {
  const days = s.days_until ?? 0;
  const hour = s.hour ?? 0;
  switch (s.key) {
    case 'upcoming': {
      const pct = days > 7 ? 8 : days > 3 ? 16 : days > 1 ? 24 : 28;

      return { pct, text: days <= 1 ? 'Arriving tomorrow' : `Arriving in ${days} days` };
    }
    case 'arrival_day':
      return { pct: 31, text: 'Arriving today' };
    case 'in_house': {
      const nights = Math.max(1, s.nights ?? 1);
      const night = Math.max(1, s.night_index ?? 1);
      const done = Math.min(1, (night - 1 + hour / 24) / nights);

      return { pct: Math.round(33.3 + 33.3 * done), text: `Night ${night} of ${nights}` };
    }
    case 'departure_day': {
      const dep = s.departure_hour && s.departure_hour > 0 ? s.departure_hour : 11;

      return { pct: Math.round(66.6 + 33.3 * Math.min(1, hour / dep)), text: 'Checking out today' };
    }
    case 'departed':
      return { pct: 100, text: 'Stay completed' };
    case 'cancelled':
      return { pct: 0, text: 'Cancelled' };
    case 'no_show':
    default:
      return { pct: 0, text: 'No-show' };
  }
};

/** 'YYYY-MM-DDTHH:mm' or 'HH:MM' → fractional hours. */
export const hourOf = (wall: string | null | undefined): number => {
  const m = /(\d{2}):(\d{2})$/.exec(String(wall || ''));

  return m ? Number(m[1]) + Number(m[2]) / 60 : 0;
};

/** The bar of a served stay at the building's current wall clock. */
export const stayJourney = (stay: Pick<StayFull, 'stage' | 'nights' | 'effective'>, buildingNow: string) =>
  journeyBar({
    key: stay.stage.key,
    days_until: stay.stage.days_until,
    night_index: stay.stage.night_index,
    nights: stay.nights,
    hour: hourOf(buildingNow),
    departure_hour: hourOf(stay.effective.departure_time),
  });

/**
 * The timing chip of a row (Konios page.tsx:904-914, on the building clock):
 * Cancelled / Archived / No-show / Departed / In house / "Arriving today at
 * 15:00" / "Tomorrow at 15:00" / "In 6 days" / "Checking out today at 11:00".
 */
export const timingText = (
  stay: Pick<StayFull, 'stage' | 'effective' | 'archived_at'> & { stage: StayStage }
): string => {
  if (stay.archived_at) return 'Archived';
  const { key, days_until: days } = stay.stage;
  if (key === 'cancelled') return 'Cancelled';
  if (key === 'no_show') return 'No-show';
  if (key === 'departed') return 'Departed';
  if (key === 'in_house') return 'In house';
  if (key === 'departure_day') return `Checking out today at ${stay.effective.departure_time}`;
  if (key === 'arrival_day') return `Arriving today at ${stay.effective.arrival_time}`;
  if (days === 1) return `Tomorrow at ${stay.effective.arrival_time}`;

  return `In ${days} days`;
};

/** "14 Oct 2026 → 17 Oct 2026" — day keys printed as they are (never re-zoned). */
export const stayDates = (checkIn: string, checkOut: string, withYear = true): string => {
  const opts: Intl.DateTimeFormatOptions = withYear
    ? { day: 'numeric', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'short' };

  return `${formatDayKey(checkIn, opts)} → ${formatDayKey(checkOut, opts)}`;
};
